<script>
export const TREATMENT_HISTORY_SCHEMA = {
    widgetType: 'treatmentHistory',
    label: 'Treatment History',
    fields: [
        { name: 'condition', type: 'string', required: true, description: 'Condition or reason for treatment' },
        {
            name: 'type',
            type: 'choice',
            required: false,
            description: 'Type of treatment history',
            options: [
                { value: 'medical', label: 'Medical/surgical' },
                { value: 'behavioral', label: 'Mental health/substance use' },
            ],
        },
        { name: 'provider', type: 'string', required: false, description: 'Provider or facility name' },
        { name: 'date', type: 'string', required: false, description: 'Date of treatment (for medical/surgical type)' },
        { name: 'startDate', type: 'string', required: false, description: 'Start date of treatment (for mental health/substance use type)' },
        { name: 'endDate', type: 'string', required: false, description: 'End date of treatment (for mental health/substance use type)' },
        {
            name: 'careSetting',
            type: 'choice',
            required: false,
            description: 'Care setting (for mental health/substance use type)',
            options: [
                { value: 'Outpatient therapy (e.g., counseling)', label: 'Outpatient therapy (e.g., counseling)' },
                { value: 'Intensive outpatient (IOP)', label: 'Intensive outpatient (IOP)' },
                { value: 'Partial hospitalization (PHP)', label: 'Partial hospitalization (PHP)' },
                { value: 'Residential treatment', label: 'Residential treatment' },
                { value: 'Acute psychiatric hospitalization', label: 'Acute psychiatric hospitalization' },
                { value: 'State hospital', label: 'State hospital' },
                { value: 'Detox/Withdrawal management', label: 'Detox/Withdrawal management' },
                { value: 'Medication management', label: 'Medication management' },
                { value: 'Case management', label: 'Case management' },
                { value: 'Peer support', label: 'Peer support' },
                { value: 'Recovery groups (AA/NA)', label: 'Recovery groups (AA/NA)' },
                { value: 'Community-based services', label: 'Community-based services' },
                { value: 'Telehealth', label: 'Telehealth' },
            ],
        },
        {
            name: 'treatmentFocus',
            type: 'choice',
            required: false,
            description: 'Treatment focus (for mental health/substance use type)',
            options: [
                { value: 'Mental health', label: 'Mental health' },
                { value: 'Substance use', label: 'Substance use' },
                { value: 'Co-occurring', label: 'Co-occurring' },
            ],
        },
        {
            name: 'treatmentResponse',
            type: 'choice',
            required: false,
            description: 'Response to treatment (for mental health/substance use type)',
            options: [
                { value: 'Completed', label: 'Completed' },
                { value: 'Improved', label: 'Improved' },
                { value: 'No change', label: 'No change' },
            ],
        },
        {
            name: 'goalsMet',
            type: 'choice',
            required: false,
            description: 'Were treatment goals met (for mental health/substance use type)',
            options: [
                { value: 'Yes', label: 'Yes' },
                { value: 'Partially', label: 'Partially' },
                { value: 'No', label: 'No' },
                { value: 'Unknown', label: 'Unknown' },
            ],
        },
    ],
};
</script>

<script setup>
import { ref, computed, watch, inject, onMounted, onBeforeUnmount } from 'vue';
import BnMenu from '@/components/BnMenu.vue';
import BnMenuItem from '@/components/BnMenuItem.vue';
import BnDialog from '@/components/BnDialog.vue';
import DataProvider from '@/components/DataProvider';
import BnDatePicker from '@/components/BnDatePicker.vue';
import { Condition, List } from '@/fhirworks';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useAuthStore } from '@/stores/auth';
import { useDisplay } from 'vuetify';
import { validateChoiceFields } from '@/components/questionnaires/widgets/widgetValidation';

const authStore = useAuthStore();
const vuetifyDisplay = useDisplay();

// Define option lists once for reuse
const careSettingItems = [
    'Outpatient therapy (e.g., counseling)',
    'Intensive outpatient (IOP)',
    'Partial hospitalization (PHP)',
    'Residential treatment',
    'Acute psychiatric hospitalization',
    'State hospital',
    'Detox/Withdrawal management',
    'Medication management',
    'Case management',
    'Peer support',
    'Recovery groups (AA/NA)',
    'Community-based services',
    'Telehealth',
    'Other: ',
];

const treatmentFocusItems = ['Mental health', 'Substance use', 'Co-occurring'];

const treatmentResponseItems = ['Completed', 'Improved', 'No change', 'Other: '];

const treatmentGoalsMetItems = ['Yes', 'Partially', 'No', 'Unknown'];

const props = defineProps({
    title: String,
    patientId: String,
    printView: Boolean,
    isLocked: Boolean,
    modelValue: [Array, Object, String],
    linkId: String,
});

const emit = defineEmits(['update:modelValue']);

const bnDialogRef = ref(null);
const conditionHistoryDP = ref(null);
const conditionData = ref([]);

const hasConditions = ref(null);
const hasMedicalSurgicalConditions = ref(null);
const hasMentalHealthSubstanceUseConditions = ref(null);

const readOnly = computed(() => {
    return props.printView || props.isLocked;
});

watch(
    () => hasConditions.value,
    () => {
        if (hasConditions.value) {
            if (!medicalSurgicalTreatmentHistory.value?.length) addNewCondition('medical');
            if (!mentalHealthSubstanceUseTreatmentHistory.value?.length) addNewCondition('behavioral');
            return;
        }
        removeAllConditions();
    },
);

watch(
    () => conditionData.value,
    () => {
        if (conditionData.value && !props.isLocked) {
            emit('update:modelValue', conditionData.value);
        }
    },
    { immediate: true, deep: true },
);

const conditionQuery = computed(() => {
    if (props.isLocked && props.modelValue) {
        return { resource: props.modelValue };
    }

    return {
        query: [
            { query: `subject=${props.patientId}&category=medical-history`, resourceType: 'Condition' },
            { query: `subject=${props.patientId}&code=conditionList`, resourceType: 'List' },
        ],
    };
});

const treatmentHistory = computed(() => {
    return conditionData.value.filter((el) => el.resourceType === 'Condition');
});

const medicalSurgicalTreatmentHistory = computed(() => {
    return conditionData.value.filter((el) => el.resourceType === 'Condition' && (el.conditionType === 'medical' || el.conditionType === undefined));
});

const mentalHealthSubstanceUseTreatmentHistory = computed(() => {
    return conditionData.value.filter((el) => el.resourceType === 'Condition' && el.conditionType === 'behavioral');
});

const conditionsLoaded = () => {
    if (conditionData.value.length) {
        hasConditions.value = !conditionData.value?.find((list) => list.resourceType === 'List' && list.code?.codeZero === 'conditionList');
    }
    hasMedicalSurgicalConditions.value = medicalSurgicalTreatmentHistory.value.length ? true : null;
    hasMentalHealthSubstanceUseConditions.value = mentalHealthSubstanceUseTreatmentHistory.value.length ? true : null;
};

const addNewCondition = (treatmentConditionType = 'medical') => {
    let conditionList = conditionData.value?.find((list) => list.resourceType === 'List' && list.code?.codeZero === 'conditionList');

    if (conditionList) {
        conditionHistoryDP.value.deleteItem(conditionList);
    }

    let treatmentHistoryRecord = new Condition({
        subject: { id: props.patientId, resourceType: 'Patient' },
        clinicalStatus: {
            coding: [{ code: 'active', system: 'http://terminology.hl7.org/CodeSystem/condition-clinical' }],
        },
        verificationStatus: {
            coding: [{ code: 'confirmed', system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status' }],
        },
        recordedDate: new Date().toISOString(),
        recorder: authStore.aidboxPersonReference,
        category: [
            {
                coding: [
                    {
                        code: 'medical-history',
                        system: 'http://terminology.hl7.org/CodeSystem/condition-category',
                        display: 'Encounter Diagnosis',
                    },
                ],
            },
        ],
        conditionType: treatmentConditionType,
    });

    conditionHistoryDP.value.save(treatmentHistoryRecord).catch(() => {}); // reported through saveError
};
const removeAllConditions = async () => {
    let emptyList = new List({
        subject: { id: props.patientId, resourceType: 'Patient' },
        status: 'current',
        mode: 'working',
        emptyReason: {
            coding: [
                {
                    system: 'http://terminology.hl7.org/CodeSystem/list-empty-reason',
                    code: 'nilknown',
                },
            ],
        },
        code: {
            coding: [
                {
                    code: 'conditionList',
                    system: 'https://bestnotes.com',
                },
            ],
        },
    });

    // Skip dialog if no saved conditions exist
    // Also skip dialog if all conditions have blank code.text
    const hasNoSavedConditions = !treatmentHistory.value.find((data) => data.resourceType === 'Condition' && data?.id);
    const allConditionsBlank = treatmentHistory.value.length > 0 && treatmentHistory.value.every((data) => !data?.code?.text);

    if (hasNoSavedConditions || allConditionsBlank) {
        for (let condition of treatmentHistory.value) {
            conditionHistoryDP.value.deleteItem(condition);
        }
        conditionHistoryDP.value.save(emptyList).catch(() => {}); // reported through saveError
        return;
    }
    const options = {
        header: 'Warning',
        message: 'This will delete all existing conditions, Continue?',
        confirm: 'Delete',
        cancel: 'Cancel',
    };
    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) {
            hasConditions.value = true;
            hasMedicalSurgicalConditions.value = medicalSurgicalTreatmentHistory.value.length ? true : null;
            hasMentalHealthSubstanceUseConditions.value = mentalHealthSubstanceUseTreatmentHistory.value.length ? true : null;
            return;
        }

        for (let condition of treatmentHistory.value) {
            conditionHistoryDP.value.deleteItem(condition);
        }
        conditionHistoryDP.value.save(emptyList).catch(() => {}); // reported through saveError
    });
};

const deleteRecord = (condition) => {
    const options = {
        header: 'Delete condition',
        message: 'Are you sure you want to delete this condition?',
        confirm: 'Yes',
        cancel: 'No',
    };
    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) return;
        let removedConditionType = condition.conditionType ? condition.conditionType : 'medical';
        await conditionHistoryDP.value.deleteItem(condition);

        if (!medicalSurgicalTreatmentHistory.value.length || !mentalHealthSubstanceUseTreatmentHistory.value.length) {
            addNewCondition(removedConditionType);
        }
    });
};

const setOnset = (record, date) => {
    record.onset = {
        string: date,
    };
};
const setPeriodOnset = (record, date, type = 'start') => {
    // Ensure record.onset exists
    if (!record.onset) {
        record.onset = {};
    }

    // Ensure onsetPeriod exists
    if (!record.onset.onsetPeriod) {
        record.onset.onsetPeriod = {};
    }

    if (type === 'start') {
        record.onset.onsetPeriod.start = date;
    } else {
        record.onset.onsetPeriod.end = date;
    }
};

// Returns true when the period on a record violates FHIR per-1 (start > end).
// Used both to display an inline error on the date pickers AND to gate
// DataProvider's autosave via the `autosaveWhen` prop, so an invalid period
// is never sent to Aidbox.
const isPeriodInvalid = (record) => {
    const start = record?.onset?.onsetPeriod?.start;
    const end = record?.onset?.onsetPeriod?.end;
    if (!start || !end) return false;
    return new Date(start) > new Date(end);
};

// DataProvider autosave gate: only save records whose period is valid.
// Records with an invalid period stay dirty in memory and surface the
// validation error to the user; once they fix the dates the next change
// triggers the autosave normally.
const canAutosaveRecord = (record) => {
    if (record?.resourceType !== 'Condition') return true;
    return !isPeriodInvalid(record);
};

const PERIOD_ERROR_MESSAGE = 'End date must be on or after the start date.';
const SAVE_FAILED_MESSAGE = 'Your latest treatment history change was not saved. Edit the entry to try again, and contact support if it keeps happening.';

const addItem = async (data) => {
    const invalidChoices = validateChoiceFields(data, TREATMENT_HISTORY_SCHEMA);
    if (invalidChoices) return invalidChoices;

    const conditionType = data.type || 'medical';
    const record = new Condition({
        subject: { id: props.patientId, resourceType: 'Patient' },
        clinicalStatus: {
            coding: [{ code: 'active', system: 'http://terminology.hl7.org/CodeSystem/condition-clinical' }],
        },
        verificationStatus: {
            coding: [{ code: 'confirmed', system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status' }],
        },
        recordedDate: new Date().toISOString(),
        recorder: authStore.aidboxPersonReference,
        category: [
            {
                coding: [
                    {
                        code: 'medical-history',
                        system: 'http://terminology.hl7.org/CodeSystem/condition-category',
                        display: 'Encounter Diagnosis',
                    },
                ],
            },
        ],
        conditionType,
    });

    if (data.condition) record.code = { text: data.condition };
    if (data.provider) record.asserter = { display: data.provider };

    if (conditionType === 'medical') {
        if (data.date) record.onset = { string: data.date };
        if (data.treatmentResponse) record.note = [{ text: data.treatmentResponse }];
    } else {
        if (data.startDate || data.endDate) {
            record.onset = { onsetPeriod: {} };
            if (data.startDate) record.onset.onsetPeriod.start = data.startDate;
            if (data.endDate) record.onset.onsetPeriod.end = data.endDate;
        }
        if (data.careSetting) record.txHistoryCareSettings = data.careSetting;
        if (data.treatmentFocus) record.txHistoryTreatmentFocus = data.treatmentFocus;
        if (data.treatmentResponse) record.note = [{ text: data.treatmentResponse }];
        if (data.goalsMet) record.txHistoryTreatmentGoalsMet = data.goalsMet;
    }

    const conditionList = conditionData.value?.find((list) => list.resourceType === 'List' && list.code?.codeZero === 'conditionList');
    if (conditionList) {
        conditionHistoryDP.value.deleteItem(conditionList);
    }

    await conditionHistoryDP.value.save(record);
    hasConditions.value = true;
    return { success: true };
};

const getItems = () => {
    return treatmentHistory.value
        .filter((r) => r.code?.text)
        .map((r) => ({
            condition: r.code?.text || '',
            type: r.conditionType || 'medical',
            provider: r.asserter?.display || '',
            date: r.onset?.string || '',
            startDate: r.onset?.onsetPeriod?.start || '',
            endDate: r.onset?.onsetPeriod?.end || '',
            careSetting: r.txHistoryCareSettings || '',
            treatmentFocus: r.txHistoryTreatmentFocus || '',
            treatmentResponse: r.note?.[0]?.text || '',
            goalsMet: r.txHistoryTreatmentGoalsMet || '',
        }));
};

defineExpose({ addItem, getItems });

const _registerWidgetApi = inject('registerWidgetApi', null);
const _unregisterWidgetApi = inject('unregisterWidgetApi', null);

onMounted(() => {
    _registerWidgetApi?.('treatmentHistory', { addItem, getItems, schema: TREATMENT_HISTORY_SCHEMA });
});

onBeforeUnmount(() => {
    _unregisterWidgetApi?.('treatmentHistory');
});
</script>

<template>
    <v-card variant="flat" :border="false" class="pb-2" :data-linkid="linkId">
        <DataProvider
            ref="conditionHistoryDP"
            v-model="conditionData"
            collection
            autosave
            :autosave-when="canAutosaveRecord"
            refresh-on-change
            v-bind="conditionQuery"
            @loaded="conditionsLoaded"
            @save-error="() => {}"
        >
            <template #loading>
                <v-skeleton-loader class="mx-auto" width="100%" type="list-item-three-line" />
            </template>
            <template #error="{ error, refresh }">
                <div>
                    {{ error }}
                    <v-btn variant="flat" color="primary" @click="refresh">Try again</v-btn>
                </div>
            </template>
            <template #default="{ saveError }">
                <div data-cy="treatmentHistory">
                    <div class="d-flex py-1 text-h2" style="color: #43a047">
                        <FontAwesomeIcon size="lg" :icon="['far', 'note-medical']" class="align-self-center mr-2"></FontAwesomeIcon>
                        <v-list-item-title class="text-h2">{{ title }}</v-list-item-title>
                    </div>

                    <!-- The provider keeps one error per record and clears it when that record next saves (#6256); the @save-error listener stands in for its own notice -->
                    <v-alert v-if="!readOnly && saveError?.some?.(Boolean)" type="error" variant="tonal" density="compact" class="mb-2" data-cy="treatmentHistorySaveError">
                        {{ SAVE_FAILED_MESSAGE }}
                    </v-alert>

                    <v-radio-group v-model="hasConditions" :readonly="readOnly" label="Do you have any prior Medical, Surgical, Mental Health, or Substance Use treatment history?" inline class="mt-0">
                        <v-radio label="Yes" :value="true" class="mr-2" data-cy="hasConditionsTrueRadio"></v-radio>
                        <v-radio label="No" :value="false" data-cy="hasConditionsFalseRadio"></v-radio>
                    </v-radio-group>

                    <div data-linkid="medicalSurgicalTreatmentHistory">
                        <div v-if="hasConditions" class="bn-section-header">Medical/surgical treatment history</div>
                        <div
                            v-for="(record, index) in medicalSurgicalTreatmentHistory"
                            :key="index"
                            style="border-left: 3px solid #43a047; opacity: 1; padding-left: 8px"
                            class="mb-2 pt-2 pb-2 d-flex flex-no-wrap"
                            :class="{ 'flex-column': vuetifyDisplay.xs.value, 'justify-space-between': !vuetifyDisplay.xs.value }"
                        >
                            <div class="d-flex">
                                <v-textarea
                                    :model-value="record.code?.text"
                                    :readonly="readOnly"
                                    label="Condition"
                                    data-cy="recordAllergenField"
                                    rows="1"
                                    auto-grow
                                    hide-details
                                    @update:model-value="(val) => (record.code = { text: val })"
                                ></v-textarea>
                                <BnMenu v-if="vuetifyDisplay.xs.value && !readOnly" offset-y left icon="ellipsis-v" class="mr-0" data-cy="recordItemMenu">
                                    <BnMenuItem icon="trash" label="Delete condition" data-cy="removeAllergyBtn" @click="deleteRecord(record)" />
                                </BnMenu>
                            </div>
                            <BnDatePicker
                                :model-value="record.onset?.string"
                                :readonly="readOnly"
                                label="Date"
                                :style="!vuetifyDisplay.xs.value ? 'width: 120px;' : ''"
                                :class="{ 'mx-2': !vuetifyDisplay.xs.value, 'mr-11': vuetifyDisplay.xs.value }"
                                :strict-date="false"
                                hide-details
                                @update:model-value="(val) => setOnset(record, val)"
                            ></BnDatePicker>
                            <v-text-field
                                :model-value="record.asserter?.display"
                                :readonly="readOnly"
                                label="Provider"
                                data-cy="recordAllergenField"
                                :style="!vuetifyDisplay.xs.value ? 'width: 160px;' : ''"
                                :class="{ 'mr-2': !vuetifyDisplay.xs.value, 'mr-11': vuetifyDisplay.xs.value }"
                                hide-details
                                @update:model-value="(val) => (record.asserter = { display: val })"
                            ></v-text-field>
                            <v-textarea
                                :model-value="record.note?.[0]?.text"
                                :readonly="readOnly"
                                :class="{ 'mr-11': vuetifyDisplay.xs.value }"
                                label="Treatment and response"
                                data-cy="recordAllergenField"
                                rows="1"
                                auto-grow
                                hide-details
                                @update:model-value="(val) => (record.note = [{ text: val }])"
                            ></v-textarea>
                            <BnMenu v-if="!vuetifyDisplay.xs.value && !readOnly" offset-y left icon="ellipsis-v" class="ml-2" data-cy="recordItemMenu">
                                <BnMenuItem icon="trash" label="Delete condition" data-cy="removeAllergyBtn" @click="deleteRecord(record)" />
                            </BnMenu>
                        </div>
                        <v-btn
                            v-if="hasConditions && !readOnly"
                            :disabled="!medicalSurgicalTreatmentHistory?.every((e) => e?.code?.text)"
                            color="primary"
                            variant="tonal"
                            class="v-list-item-pointer-hover ml-2 mb-4"
                            @click="addNewCondition('medical')"
                        >
                            <template #prepend>
                                <FontAwesomeIcon :icon="['far', 'plus']" size="sm" />
                            </template>
                            Add another condition
                        </v-btn>
                    </div>

                    <div data-linkid="mentalHealthSubstanceUseTreatmentHistory">
                        <div v-if="hasConditions" class="bn-section-header">Mental health/substance use treatment history</div>

                        <div
                            v-for="(record, index) in mentalHealthSubstanceUseTreatmentHistory"
                            :key="index"
                            style="border-left: 3px solid #43a047; opacity: 1; padding-left: 8px"
                            class="mb-4 pt-2"
                            :class="{ 'flex-column': vuetifyDisplay.xs.value, 'justify-space-between': !vuetifyDisplay.xs.value }"
                        >
                            <div class="d-flex">
                                <v-textarea
                                    :model-value="record.code?.text"
                                    :readonly="readOnly"
                                    label="Reason for treatment"
                                    data-cy="recordAllergenField"
                                    rows="1"
                                    auto-grow
                                    @update:model-value="(val) => (record.code = { text: val })"
                                ></v-textarea>
                                <BnMenu v-if="!readOnly" offset-y left icon="ellipsis-v" class="mr-0" data-cy="recordItemMenu">
                                    <BnMenuItem icon="trash" label="Delete condition" data-cy="removeAllergyBtn" @click="deleteRecord(record)" />
                                </BnMenu>
                            </div>

                            <v-row>
                                <v-col cols="12">
                                    <v-textarea
                                        :model-value="record.asserter?.display"
                                        :readonly="readOnly"
                                        label="Provider/facility name"
                                        data-cy="recordAllergenField"
                                        hide-details
                                        rows="1"
                                        auto-grow
                                        @update:model-value="(val) => (record.asserter = { display: val })"
                                    ></v-textarea>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" sm="6">
                                    <BnDatePicker
                                        :model-value="record.onset?.onsetPeriod?.start"
                                        :readonly="readOnly"
                                        label="Start date of treatment"
                                        :style="!vuetifyDisplay.xs.value ? 'width: 120px;' : ''"
                                        :strict-date="false"
                                        @update:model-value="(val) => setPeriodOnset(record, val, 'start')"
                                    ></BnDatePicker>
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <BnDatePicker
                                        :model-value="record.onset?.onsetPeriod?.end"
                                        :readonly="readOnly"
                                        label="End date of treatment"
                                        :style="!vuetifyDisplay.xs.value ? 'width: 120px;' : ''"
                                        :strict-date="false"
                                        :error-messages="isPeriodInvalid(record) ? PERIOD_ERROR_MESSAGE : ''"
                                        @update:model-value="(val) => setPeriodOnset(record, val, 'end')"
                                    ></BnDatePicker>
                                </v-col>
                            </v-row>

                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-combobox
                                        v-model="record.txHistoryCareSettings"
                                        label="Care setting"
                                        :readonly="readOnly"
                                        :items="careSettingItems"
                                        :data-options="JSON.stringify(careSettingItems)"
                                        hide-details
                                    ></v-combobox>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-combobox
                                        v-model="record.txHistoryTreatmentFocus"
                                        label="Treatment focus"
                                        :readonly="readOnly"
                                        :items="treatmentFocusItems"
                                        :data-options="JSON.stringify(treatmentFocusItems)"
                                        hide-details
                                    ></v-combobox>
                                </v-col>
                            </v-row>

                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-combobox
                                        :model-value="record.note?.[0]?.text"
                                        :readonly="readOnly"
                                        :items="treatmentResponseItems"
                                        :data-options="JSON.stringify(treatmentResponseItems)"
                                        label="Response to treatment"
                                        data-cy="recordAllergenField"
                                        hide-details
                                        @update:model-value="(val) => (record.note = [{ text: val }])"
                                    ></v-combobox>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-combobox
                                        v-model="record.txHistoryTreatmentGoalsMet"
                                        :readonly="readOnly"
                                        :items="treatmentGoalsMetItems"
                                        :data-options="JSON.stringify(treatmentGoalsMetItems)"
                                        label="Treatment goals met"
                                        data-cy="recordAllergenField"
                                        hide-details
                                    ></v-combobox>
                                </v-col>
                            </v-row>
                        </div>
                        <v-btn
                            v-if="hasConditions && !readOnly"
                            :disabled="!mentalHealthSubstanceUseTreatmentHistory?.every((e) => e?.code?.text)"
                            color="primary"
                            variant="tonal"
                            class="v-list-item-pointer-hover ml-2"
                            @click="addNewCondition('behavioral')"
                        >
                            <template #prepend>
                                <FontAwesomeIcon :icon="['far', 'plus']" size="sm" />
                            </template>
                            Add Treatment Instance
                        </v-btn>
                    </div>
                </div>
            </template>
        </DataProvider>
        <BnDialog ref="bnDialogRef" />
    </v-card>
</template>
